import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { Team } from '@core/models';
import { TableAvatarGroupRendererComponent } from 'tsui/lib/ts-table/renderer-components/table-avatar-group-renderer/table-avatar-group-renderer.component';
import { TableActionsRendererComponent, TsTableComponent } from 'tsui/lib/ts-table';
import { GridApi, IRowNode, IsGroupOpenByDefaultParams } from 'ag-grid-enterprise';
import { JsonPipe } from '@angular/common';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { TeamsService } from '../services/teams.service';

@Component({
  selector: 'teams-grid',
  standalone: true,
  imports: [AgGridAngular, TsTableComponent, JsonPipe],
  templateUrl: './teams-grid.component.html',
  styleUrl: './teams-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamsGridComponent {
  @Input({ required: true }) set teams(value: Team[]) {
    if (!value) return;
    this.flattenedTeamsWithPathInfo = TeamsService.flattenTeamsAndAddMetadata(value);
  }

  @Input() set loading(val: boolean) {
    if (this.gridApi) {
      this.gridApi.setGridOption('loading', val);
    }
  }

  @Output() editTeam = new EventEmitter<Team>();
  @Output() deleteTeam = new EventEmitter<Team>();

  protected flattenedTeamsWithPathInfo: (Team & { path: string[] })[];
  private gridApi!: GridApi<Team>;

  gridOptions = {
    loading: true,
    // keeps root team opened by default
    isGroupOpenByDefault: (params: IsGroupOpenByDefaultParams) => {
      return !(params.rowNode.data as Team).parent_id;
    },
  };
  colDefs: ColDef[] = [
    {
      field: 'users',
      headerName: 'Members',
      sortable: false,
      maxWidth: 800,
      resizable: false,
      suppressHeaderMenuButton: true,
      cellRenderer: TableAvatarGroupRendererComponent,
    },
    {
      headerName: '',
      field: 'actions',
      sortable: false,
      lockPosition: 'right',
      resizable: false,
      suppressMovable: true,
      width: 50,
      suppressHeaderMenuButton: true,
      cellRenderer: TableActionsRendererComponent,
      cellRendererParams: {
        actions: <TableActions[]>[
          {
            icon: 'ts_pen',
            label: 'Edit',
            action: (team: Team) => this.editTeam.emit(team),
          },
          {
            icon: 'ts_trash-alt',
            label: 'Delete',
            variant: 'danger',
            displayTooltip: ({ data }: IRowNode<Team>) =>
              !data?.parent_id ? 'This team is the root team and cannot be deleted' : undefined,
            // with this we are disabling the root team (it has no parent_id)
            isDisabled: ({ data }: IRowNode<Team>) => !data?.parent_id,
            action: (team: Team) => this.deleteTeam.emit(team),
          },
        ],
      },
      cellStyle: {
        overflow: 'visible',
        'text-overflow': 'clip',
        'white-space': 'nowrap',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
  ];

  autoGroupColumnDef: ColDef = {
    headerName: 'Name',
    field: 'name',
    resizable: false,
    flex: 1,
    suppressHeaderMenuButton: true,
    sortable: false,
    cellRendererParams: {
      suppressCount: true,
    },
  };

  getDataPath(data: any) {
    return data.path;
  }

  onGridReady(params: GridReadyEvent<Team>) {
    this.gridApi = params.api;
  }
}
